import React, { useMemo, useId, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush
} from 'recharts';
import { chartTheme } from './chartTheme';
import { buildAreaGradientDefs } from './chartGradientUtils';
import {
  defaultDateTickFormatter,
  defaultDateTooltipFormatter,
  defaultValueFormatter
} from './formatters';
import { YChartTooltip } from './YChartTooltip';
import { buildBrushDetails } from './rangeUtils';
import { toggleLegendKey } from './legendUtils';

const SERIES_KEYS = ['feedInventoryLbs', 'deerCount'];

/** Room for legend + dual Y-axis ticks (matches deer timeline charts). */
const FEED_OUTLOOK_MARGIN = { top: 28, right: 48, left: 0, bottom: 0 };
const LEFT_AXIS_WIDTH = 52;
const RIGHT_AXIS_WIDTH = 40;

function formatFeedInventoryTick(value) {
  if (!Number.isFinite(value)) return value;
  return Math.max(0, Math.round(value));
}

const OUTLOOK_SERIES = [
  {
    name: 'Total feed remaining',
    dataKey: 'feedInventoryLbs',
    yAxisId: 'left',
    color: 'var(--color-brand-accent)',
    strokeWidth: 2
  },
  {
    name: 'Deer sightings',
    dataKey: 'deerCount',
    yAxisId: 'right',
    color: 'var(--chart-series-1)',
    strokeWidth: 1.5
  }
];

/**
 * Feed remaining vs deer sightings — dual-axis area chart with gradient fills and legend toggle.
 */
export function YFeedOutlookChart({
  data = [],
  height = 240,
  xKey = 'date',
  xTickFormatter = defaultDateTickFormatter,
  tooltipLabelFormatter = defaultDateTooltipFormatter,
  tooltipValueFormatter = defaultValueFormatter,
  legend = true,
  enableLegendToggle = true,
  onLegendToggle,
  brush,
  brushSummary,
  brushActions,
  dataTestId = 'feed-inventory-outlook-chart',
  gradientInstanceId = 'feed-outlook'
}) {
  const reactId = useId();
  const instanceId = gradientInstanceId || reactId.replace(/:/g, '');

  const [activeKeys, setActiveKeys] = useState(SERIES_KEYS);

  useEffect(() => {
    setActiveKeys(SERIES_KEYS);
  }, []);

  const gradientDefs = useMemo(
    () => buildAreaGradientDefs(OUTLOOK_SERIES, instanceId),
    [instanceId]
  );

  const fillByDataKey = useMemo(() => {
    const map = {};
    gradientDefs.forEach((grad) => {
      map[grad.key] = `url(#${grad.id})`;
    });
    return map;
  }, [gradientDefs]);

  const strokeByDataKey = useMemo(() => {
    const map = {};
    gradientDefs.forEach((grad) => {
      map[grad.key] = grad.stroke;
    });
    return map;
  }, [gradientDefs]);

  const handleLegendClick = useCallback(
    (entry) => {
      const key = entry?.dataKey || entry?.value;
      setActiveKeys((prev) => {
        const next = toggleLegendKey(prev, key);
        if (next !== prev && onLegendToggle) {
          onLegendToggle(next);
        }
        return next;
      });
    },
    [onLegendToggle]
  );

  const legendFormatter = useCallback(
    (value, entry) => {
      const key = entry?.dataKey || value;
      const isActive = !enableLegendToggle || activeKeys.includes(key);
      return <span style={{ opacity: isActive ? 1 : 0.5 }}>{value}</span>;
    },
    [activeKeys, enableLegendToggle]
  );

  const handleBrushChange = (range) => {
    const details = buildBrushDetails(range, data, xKey);
    if (brush?.onChange) brush.onChange(details);
  };

  const hasData = Array.isArray(data) && data.length > 0;
  const hasSelection =
    typeof brush?.selection?.startIndex === 'number' &&
    typeof brush?.selection?.endIndex === 'number';
  const defaultEndIndex = hasData ? data.length - 1 : undefined;
  const brushStartIndex = hasData
    ? hasSelection
      ? brush.selection.startIndex
      : 0
    : undefined;
  const brushEndIndex = hasData
    ? hasSelection
      ? brush.selection.endIndex
      : defaultEndIndex
    : undefined;

  const axisTickStyle = {
    fontSize: chartTheme.axisFontSize,
    fill: chartTheme.axisColor,
    fontFamily: 'var(--font-mono)'
  };

  return (
    <div className="y-feed-outlook-chart">
      <div className="y-chart y-chart--feed-outlook" style={{ height }} data-testid={dataTestId}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={FEED_OUTLOOK_MARGIN}>
            <defs>
              {gradientDefs.map((grad) => (
                <linearGradient key={grad.id} id={grad.id} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={grad.stroke} stopOpacity={grad.topOpacity} />
                  <stop offset="95%" stopColor={grad.stroke} stopOpacity={grad.bottomOpacity} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} vertical={false} />
            <XAxis
              dataKey={xKey}
              tickFormatter={xTickFormatter}
              axisLine={false}
              tickLine={chartTheme.tickLine}
              tick={axisTickStyle}
              tickMargin={8}
              minTickGap={24}
            />
            <YAxis
              yAxisId="left"
              width={LEFT_AXIS_WIDTH}
              tickFormatter={formatFeedInventoryTick}
              axisLine={false}
              tickLine={chartTheme.tickLine}
              tick={axisTickStyle}
              allowDecimals={false}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              width={RIGHT_AXIS_WIDTH}
              axisLine={false}
              tickLine={chartTheme.tickLine}
              tick={axisTickStyle}
              allowDecimals={false}
            />
            <Tooltip
              cursor={false}
              content={
                <YChartTooltip
                  labelFormatter={tooltipLabelFormatter}
                  valueFormatter={tooltipValueFormatter}
                />
              }
            />
            {legend && (
              <Legend
                verticalAlign="top"
                align="left"
                iconType="circle"
                iconSize={8}
                onClick={enableLegendToggle ? handleLegendClick : undefined}
                formatter={legendFormatter}
                wrapperStyle={{
                  paddingBottom: 10,
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.04em',
                  cursor: enableLegendToggle ? 'pointer' : undefined
                }}
              />
            )}
            {OUTLOOK_SERIES.map((series) => {
              const key = series.dataKey;
              const isActive = !legend || activeKeys.includes(key);
              return (
                <Area
                  key={key}
                  yAxisId={series.yAxisId}
                  type="monotone"
                  dataKey={key}
                  name={series.name}
                  stroke={strokeByDataKey[key] || series.color}
                  fill={fillByDataKey[key] || series.color}
                  strokeWidth={series.strokeWidth}
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                  isAnimationActive={false}
                  hide={!isActive}
                />
              );
            })}
            {brush?.enabled && hasData && (
              <Brush
                key={brush.version || 0}
                dataKey={xKey}
                height={24}
                stroke={chartTheme.accent}
                travellerWidth={10}
                startIndex={brushStartIndex}
                endIndex={brushEndIndex}
                onChange={handleBrushChange}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {brushSummary ? (
        <div className="y-chart-range-summary">
          <span>{brushSummary}</span>
          {brushActions}
        </div>
      ) : null}
    </div>
  );
}

YFeedOutlookChart.propTypes = {
  data: PropTypes.array,
  height: PropTypes.number,
  xKey: PropTypes.string,
  xTickFormatter: PropTypes.func,
  tooltipLabelFormatter: PropTypes.func,
  tooltipValueFormatter: PropTypes.func,
  legend: PropTypes.bool,
  enableLegendToggle: PropTypes.bool,
  onLegendToggle: PropTypes.func,
  brush: PropTypes.shape({
    enabled: PropTypes.bool,
    selection: PropTypes.shape({
      startIndex: PropTypes.number,
      endIndex: PropTypes.number
    }),
    onChange: PropTypes.func,
    version: PropTypes.number
  }),
  brushSummary: PropTypes.string,
  brushActions: PropTypes.node,
  dataTestId: PropTypes.string,
  gradientInstanceId: PropTypes.string
};

export default YFeedOutlookChart;
